import { faClose, faGear, faQrcode } from "@fortawesome/free-solid-svg-icons"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import InputField from "../../../components/InputField/InputField"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { SimpleToggle } from "../../../components/SimpleToggle/SimpleToggle"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { API_ROUTES } from "../../../configuration/RoutesApi"
import { PATH_MAP } from "../../../configuration/PathMap"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import { companyMemory } from "../../../Objects/Memory/Memory"
import { CompanyAddressPupop } from "../MyCompany/CompanyAddressPupop"
import { LinkPublicMenuPupop } from "../MyCompany/LinkPublicMenuPupop"
import type { TimeClockSettings } from "../../../Objects/TimeClock/TimeClockSettings"
import type { TimeClockScheduleModelType } from "../../../Objects/TimeClock/TimeClockScheduleModelType"
import type { TimeClockFailurePolicyType } from "../../../Objects/TimeClock/TimeClockFailurePolicyType"
import type { TimeClockValidationType } from "../../../Objects/TimeClock/TimeClockValidationType"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import GetSpace from "../../../utils/space"

interface TimeClockSettingsPopupProps {
  settings: TimeClockSettings
  onClose: () => void
  onSave: (settings: TimeClockSettings) => Promise<boolean>
}

export const TimeClockSettingsPopup: React.FC<TimeClockSettingsPopupProps> = ({ settings, onClose, onSave }) => {
  const { t } = useTranslation(["timeClock", "buttons", "helpPopup"])

  const [form, setForm] = useState<TimeClockSettings>({ ...settings })

  const [isSaving, setIsSaving] = useState(false)

  const [isAddressOpen, setIsAddressOpen] = useState(false)

  const [isSlugOpen, setIsSlugOpen] = useState(false)

  const [, forceRefresh] = useState(0)

  const hasResolvedLocation =
    companyMemory.addressEntity?.latitude != null && companyMemory.addressEntity?.longitude != null

  const requiresQrCode = form.validationTypes.includes("QrCode")

  const publicQrCodeLink = `${API_ROUTES.SITE.replace(/\/$/, "")}${PATH_MAP.TIME_CLOCK_PUBLIC_CODE_LINK(companyMemory.slug)}`

  const toggleValidation = (validation: TimeClockValidationType, enabled: boolean) =>
    setForm((current) => ({
      ...current,
      validationTypes: enabled
        ? [...current.validationTypes.filter((item) => item !== validation), validation]
        : current.validationTypes.filter((item) => item !== validation),
    }))

  const has = (validation: TimeClockValidationType) => form.validationTypes.includes(validation)

  const handleSubmit = async () => {
    if (isSaving) return

    setIsSaving(true)

    try {
      await onSave(form)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <>
      <ModalPortal>
        <div className={defaultStyle.overlay}>
          <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
            <div className={defaultStyle.alert1}>
              <TitlePageResponsiveIconSmall
                title={t("settings.title")}
                icon={faGear}
                iconColor={THEME.colors.iconBGColor}
                children={
                  <div className={`${defaultStyle.ButtonsActions} flex items-center gap-1`}>
                    <PopupHelpButton
                      variant="global"
                      helpKey="pages.time-clock-settings"
                      title={t("helpPopup:global.timeClockSettings.title")}
                      description={t("helpPopup:global.timeClockSettings.description")}
                      bullets={t("helpPopup:global.timeClockSettings.bullets", { returnObjects: true }) as string[]}
                    />

                    <ButtonIcon
                      sizeNumber={SIZE.buttonCloseIconSize}
                      bgColor={THEME.colors.buttonBGCloseColor}
                      iconColor={THEME.colors.buttonFGCloseColor}
                      icon={faClose}
                      onClick={onClose}
                      size="small"
                      descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockSettingsPopup.button.onClose"
                    />
                  </div>
                }
              />

              <GetSpace top={10} />

              <form
                className="flex flex-col gap-3 p-2"
                onSubmit={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  void handleSubmit()
                }}
              >
                <SimpleToggle
                  title={t("settings.is_enabled")}
                  value={form.isEnabled}
                  onChange={(value) => setForm((current) => ({ ...current, isEnabled: value }))}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockSettingsPopup.toggle.isEnabled"
                />

                <div className={defaultStyle.divisionBar}></div>

                <SimpleToggle
                  title={t("settings.validation_geolocation")}
                  value={has("Geolocation")}
                  onChange={(value) => toggleValidation("Geolocation", value)}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockSettingsPopup.toggle.geolocation"
                />

                <SimpleToggle
                  title={t("settings.validation_pin")}
                  value={has("Pin")}
                  onChange={(value) => toggleValidation("Pin", value)}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockSettingsPopup.toggle.pin"
                />

                <SimpleToggle
                  title={t("settings.validation_selfie")}
                  value={has("Selfie")}
                  onChange={(value) => toggleValidation("Selfie", value)}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockSettingsPopup.toggle.selfie"
                />

                <SimpleToggle
                  title={t("settings.validation_qrcode")}
                  value={has("QrCode")}
                  onChange={(value) => toggleValidation("QrCode", value)}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockSettingsPopup.toggle.qrCode"
                />

                {requiresQrCode && (
                  <Animate type={AnimationTypes.slideUpIn} duration="1.6s">
                    <div className="flex flex-col gap-2 rounded-2xl border-2 border-gray-200 bg-gradient-to-r from-orange-50 to-white p-3">
                      <span className="flex items-center gap-2 text-sm font-extrabold text-gray-900">
                        <FontAwesomeIcon icon={faQrcode} className="text-orange-700" />

                        {t("management.qr_code")}
                      </span>

                      <p className="text-xs font-medium text-gray-600">{t("management.qr_code_public_description")}</p>

                      {!companyMemory.slug ? (
                        <>
                          <p className="rounded-lg border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                            {t("settings.slug_missing")}
                          </p>

                          <div className="flex flex-wrap gap-2">
                            <Button
                              typeButton="button"
                              size="small"
                              label={t("settings.configure_slug")}
                              bgColor={THEME.colors.buttonBGPlusColor}
                              iconColor={THEME.colors.iconBGSecondaryColor}
                              onClick={() => setIsSlugOpen(true)}
                              descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockSettingsPopup.button.configureSlug"
                            />
                          </div>
                        </>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          <Button
                            typeButton="button"
                            size="small"
                            label={t("management.qr_code_public_open")}
                            bgColor={THEME.colors.buttonBGRegisterColor}
                            iconColor={THEME.colors.buttonFGSaveColor}
                            onClick={() => window.open(publicQrCodeLink, "_blank")}
                            descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockSettingsPopup.button.openPublicQrCode"
                          />

                          <Button
                            typeButton="button"
                            size="small"
                            label={t("buttons:button_copy")}
                            bgColor={THEME.colors.buttonBGPlusColor}
                            iconColor={THEME.colors.buttonFGSaveColor}
                            onClick={async () => await navigator.clipboard.writeText(publicQrCodeLink)}
                            descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockSettingsPopup.button.copyPublicQrCode"
                          />
                        </div>
                      )}
                    </div>
                  </Animate>
                )}

                {has("Geolocation") && (
                  <Animate type={AnimationTypes.slideUpIn} duration="1.7s">
                    <div className="flex flex-col gap-3">
                      {!hasResolvedLocation && (
                        <p className="rounded-lg border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                          {t("settings.location_missing")}
                        </p>
                      )}

                      <InputField
                        type="number"
                        value={String(form.geofenceRadiusMeters)}
                        min={2}
                        max={5000}
                        placeholder={t("settings.geofence_radius") + "Ex: 150"}
                        onChange={(event) =>
                          setForm((current) => ({ ...current, geofenceRadiusMeters: Number(event.target.value) }))
                        }
                        descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockSettingsPopup.input.geofenceRadius"
                      />

                      <Animate type={AnimationTypes.slideUpIn} duration="1.5s">
                        <p className="text-xs text-slate-500">{t("settings.location_hint")}</p>
                      </Animate>

                      <Button
                        typeButton="button"
                        size="small"
                        label={t("settings.edit_address")}
                        bgColor={THEME.colors.buttonBGPlusColor}
                        iconColor={THEME.colors.iconBGSecondaryColor}
                        onClick={() => setIsAddressOpen(true)}
                        descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockSettingsPopup.button.editAddress"
                      />
                    </div>
                  </Animate>
                )}

                <div className={defaultStyle.divisionBar}></div>

                <GenericDropDown
                  initialState={form.failurePolicy}
                  data={[
                    { label: t("settings.failure_policy_pending"), value: "RegisterAsPending" },
                    { label: t("settings.failure_policy_block"), value: "Block" },
                  ]}
                  handlerFuncion={(event) =>
                    setForm((current) => ({
                      ...current,
                      failurePolicy: event.target.value as TimeClockFailurePolicyType,
                    }))
                  }
                  descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockSettingsPopup.dropdown.failurePolicy"
                />

                <InputField
                  type="number"
                  value={String(form.duplicateWindowSeconds)}
                  min={0}
                  max={900}
                  title={t("settings.duplicate_window")}
                  placeholder="60"
                  onChange={(event) =>
                    setForm((current) => ({ ...current, duplicateWindowSeconds: Number(event.target.value) }))
                  }
                  descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockSettingsPopup.input.duplicateWindow"
                />

                <div className={defaultStyle.divisionBar}></div>

                <GenericDropDown
                  initialState={form.scheduleModel}
                  data={[
                    { label: t("settings.schedule_model_none"), value: "None" },
                    { label: t("settings.schedule_model_daily"), value: "DailyWorkload" },
                    { label: t("settings.schedule_model_weekly"), value: "WeeklySchedule" },
                  ]}
                  handlerFuncion={(event) =>
                    setForm((current) => ({
                      ...current,
                      scheduleModel: event.target.value as TimeClockScheduleModelType,
                    }))
                  }
                  descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockSettingsPopup.dropdown.scheduleModel"
                />

                {form.scheduleModel !== "None" && (
                  <InputField
                    type="number"
                    value={String(form.toleranceMinutes)}
                    min={0}
                    max={120}
                    title={t("settings.tolerance")}
                    placeholder="10"
                    onChange={(event) =>
                      setForm((current) => ({ ...current, toleranceMinutes: Number(event.target.value) }))
                    }
                    descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockSettingsPopup.input.tolerance"
                  />
                )}

                <div className={defaultStyle.divisionBar}></div>

                <div className={defaultStyle.buttonsController}>
                  <Button
                    typeButton="submit"
                    label={t("settings.save")}
                    bgColor={THEME.colors.buttonBGSaveColor}
                    iconColor={THEME.colors.iconBGSecondaryColor}
                    disabled={isSaving}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockSettingsPopup.button.save"
                  />
                </div>
              </form>
            </div>
          </Animate>
        </div>
      </ModalPortal>

      {isAddressOpen && (
        <CompanyAddressPupop
          onClose={() => {
            setIsAddressOpen(false)
            forceRefresh((current) => current + 1)
          }}
        />
      )}

      {isSlugOpen && (
        <LinkPublicMenuPupop
          onClonse={() => {
            setIsSlugOpen(false)
            forceRefresh((current) => current + 1)
          }}
        />
      )}
    </>
  )
}
